// src/components/ReviewsSection.jsx - FIXED Date Handling

import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Autoplay,
  Pagination,
  Navigation,
  EffectCreative,
} from 'swiper/modules';
import {
  FiStar,
  FiUser,
  FiBriefcase,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiMessageCircle,
} from 'react-icons/fi';
import { TfiQuoteRight, TfiQuoteLeft } from 'react-icons/tfi';
import StarRating from './StarRating';
import RatingDistribution from './RatingDistribution';
import ReviewFormModal from './ReviewFormModal';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import reviewService from '../services/reviewService';

// =============================================
// HELPER: Format Date - Handles ALL formats
// =============================================
const formatReviewDate = dateValue => {
  if (!dateValue) return '';

  try {
    let date;

    // Check if it's a Firestore Timestamp (has _seconds and _nanoseconds)
    if (dateValue._seconds !== undefined) {
      date = new Date(dateValue._seconds * 1000);
    }
    // Check if it's a Firestore Timestamp (has toDate method)
    else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    }
    // Check if it's an ISO string
    else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    }
    // Check if it's already a Date object
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    // Check if it's a number (timestamp in milliseconds)
    else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    }
    // If all else fails, try to convert
    else {
      date = new Date(dateValue);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateValue);
      return '';
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const swiperRef = useRef(null);

  const fetchReviews = async () => {
    setLoading(true);
    setFetchError(null);

    try {
      console.log('🔄 [ReviewsSection] Fetching reviews...');

      const response = await fetch('/api/reviews?action=list&limit=20');
      const data = await response.json();

      console.log('📊 [ReviewsSection] API Response:', data);

      if (data.success) {
        const reviewsData = data.reviews || [];
        const statsData = data.stats || {
          totalReviews: 0,
          averageRating: 0,
          ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        };

        // FIX: Ensure all reviews have valid data
        const validatedReviews = reviewsData.map(review => ({
          ...review,
          // Ensure rating is a number
          rating:
            typeof review.rating === 'number'
              ? review.rating
              : parseInt(review.rating) || 0,
          // Preserve createdAt as-is (will be formatted in ReviewCard)
          createdAt: review.createdAt || null,
        }));

        console.log('📊 [ReviewsSection] Reviews:', validatedReviews.length);
        console.log(
          '📊 [ReviewsSection] First review date:',
          validatedReviews[0]?.createdAt,
        );

        setReviews(validatedReviews);

        // FIX: Ensure ratingDistribution has all keys
        const safeStats = {
          totalReviews: statsData.totalReviews || 0,
          averageRating: statsData.averageRating || 0,
          ratingDistribution: {
            5: statsData.ratingDistribution?.[5] || 0,
            4: statsData.ratingDistribution?.[4] || 0,
            3: statsData.ratingDistribution?.[3] || 0,
            2: statsData.ratingDistribution?.[2] || 0,
            1: statsData.ratingDistribution?.[1] || 0,
          },
        };

        setStats(safeStats);
      } else {
        console.error('❌ [ReviewsSection] API Error:', data.message);
        setFetchError(data.message || 'Failed to fetch reviews');

        setStats({
          totalReviews: 0,
          averageRating: 0,
          ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        });
      }
    } catch (error) {
      console.error('❌ [ReviewsSection] Error fetching reviews:', error);
      setFetchError(error.message);

      setStats({
        totalReviews: 0,
        averageRating: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleReviewSubmit = async formData => {
    try {
      const result = await reviewService.submitReview(
        {
          name: formData.get('name'),
          email: formData.get('email'),
          occupation: formData.get('occupation'),
          serviceType: formData.get('serviceType'),
          rating: parseInt(formData.get('rating')),
          message: formData.get('message'),
        },
        formData.get('image'),
      );

      if (result) {
        await fetchReviews();
        return { success: true };
      }
      return { success: false, error: 'Failed to submit review' };
    } catch (error) {
      console.error('Error submitting review:', error);
      return { success: false, error: error.message };
    }
  };

  // Build ratingsData with guaranteed values
  const ratingsData = [
    { stars: 5, count: stats.ratingDistribution?.[5] ?? 0 },
    { stars: 4, count: stats.ratingDistribution?.[4] ?? 0 },
    { stars: 3, count: stats.ratingDistribution?.[3] ?? 0 },
    { stars: 2, count: stats.ratingDistribution?.[2] ?? 0 },
    { stars: 1, count: stats.ratingDistribution?.[1] ?? 0 },
  ];

  console.log('📊 [ReviewsSection] Ratings Data:', ratingsData);
  console.log('📊 [ReviewsSection] Total Reviews:', stats.totalReviews);
  console.log('📊 [ReviewsSection] Average Rating:', stats.averageRating);

  // =============================================
  // REVIEW CARD - FIXED Date Handling
  // =============================================
  const ReviewCard = ({ review, index }) => {
    const isHovered = hoveredCard === index;
    const formattedDate = formatReviewDate(review.createdAt);

    // Debug log to see what we're getting
    console.log(
      `📅 Review ${index} date:`,
      review.createdAt,
      'Formatted:',
      formattedDate,
    );

    return (
      <div
        className="relative rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer"
        style={{
          backgroundColor: 'var(--bg)',
          border: `1px solid ${isHovered ? 'var(--text)' : 'var(--border)'}`,
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: isHovered
            ? '0 20px 40px -12px rgba(0, 0, 0, 0.25)'
            : 'none',
        }}
        onMouseEnter={() => setHoveredCard(index)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <div
          className={`absolute inset-0 transition-opacity duration-500 pointer-events-none ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
        </div>

        <div className="relative p-6 md:p-7">
          <div className="absolute top-5 right-5 opacity-20">
            <TfiQuoteRight
              className="w-10 h-10"
              style={{ color: 'var(--text)' }}
            />
          </div>

          <div className="flex items-start gap-4 mb-5">
            <div className="relative">
              <div
                className={`absolute inset-0 rounded-full transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
              >
                <div className="absolute inset-[-2px] rounded-full bg-gradient-to-r from-white/20 to-white/5" />
              </div>
              {review.imageUrl ? (
                <img
                  src={review.imageUrl}
                  alt={review.name}
                  className="w-12 h-12 rounded-full object-cover ring-1 ring-white/10"
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <FiUser
                    className="w-5 h-5"
                    style={{ color: 'var(--text)' }}
                  />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3
                className="text-lg font-bold tracking-tight"
                style={{ color: 'var(--text)' }}
              >
                {review.name}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {review.occupation && (
                  <span
                    className="text-xs flex items-center gap-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiBriefcase className="w-3 h-3" />
                    {review.occupation}
                  </span>
                )}
                {formattedDate && (
                  <span
                    className="text-xs flex items-center gap-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiCalendar className="w-3 h-3" />
                    {formattedDate}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <StarRating rating={review.rating} />
          </div>

          {review.serviceType && (
            <div className="mb-3">
              <span
                className="text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiBriefcase className="w-3 h-3" />
                {review.serviceType}
              </span>
            </div>
          )}

          <div className="relative">
            <TfiQuoteLeft
              className="absolute -top-1 -left-1 w-3 h-3 opacity-30"
              style={{ color: 'var(--text)' }}
            />
            <p
              className="text-sm leading-relaxed pl-4 line-clamp-4"
              style={{ color: 'var(--muted)' }}
            >
              {review.message}
            </p>
          </div>

          <div
            className="mt-5 pt-4 border-t transition-all duration-300"
            style={{ borderColor: 'var(--border)' }}
          >
            <div
              className={`flex items-center gap-2 transition-all duration-300 ${isHovered ? 'translate-x-1' : ''}`}
            >
              <div className="w-6 h-px bg-gradient-to-r from-white/40 to-transparent" />
              <span
                className="text-xs uppercase tracking-wider opacity-60"
                style={{ color: 'var(--text)' }}
              >
                Verified Client
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SkeletonCard = () => (
    <div
      className="rounded-2xl overflow-hidden animate-pulse"
      style={{
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
      }}
    >
      <div className="p-6 md:p-7">
        <div className="flex items-start gap-4 mb-5">
          <div
            className="w-12 h-12 rounded-full"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <div className="flex-1">
            <div
              className="h-5 w-28 rounded mb-2"
              style={{ backgroundColor: 'var(--border)' }}
            />
            <div
              className="h-3 w-20 rounded"
              style={{ backgroundColor: 'var(--border)' }}
            />
          </div>
        </div>
        <div
          className="h-4 w-24 rounded mb-4"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="h-4 w-full rounded mb-2"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="h-4 w-3/4 rounded mb-2"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="h-4 w-1/2 rounded"
          style={{ backgroundColor: 'var(--border)' }}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div
          className="text-center py-12 rounded-2xl border"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
          }}
        >
          <FiMessageCircle
            className="w-12 h-12 mx-auto mb-3 opacity-50"
            style={{ color: 'var(--muted)' }}
          />
          <p style={{ color: 'var(--muted)' }}>Unable to load reviews</p>
          <button
            onClick={fetchReviews}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const approvedReviews = reviews.filter(r => r.status === 'approved');

  return (
    <div className="relative py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, var(--text) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <FiStar className="w-4 h-4 text-yellow-400 fill-current" />
            <span
              className="text-sm uppercase tracking-wider"
              style={{ color: 'var(--text)' }}
            >
              Client Testimonials
            </span>
          </div>
          <h2
            className="text-3xl md:text-5xl font-bold mb-4 tracking-tight"
            style={{ color: 'var(--text)' }}
          >
            What Our
            <span className="relative inline-block mx-2">
              Clients Say
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="2"
                viewBox="0 0 200 2"
              >
                <path
                  d="M0 1 L200 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="3 3"
                />
              </svg>
            </span>
          </h2>
          <p
            className="text-base max-w-2xl mx-auto"
            style={{ color: 'var(--muted)' }}
          >
            Real experiences from real clients. See why people trust me with
            their creative projects.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div
            className="relative group rounded-2xl p-6 text-center transition-all duration-300 hover:scale-[1.01]"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="relative">
              <div
                className="text-5xl md:text-6xl font-bold mb-3"
                style={{ color: 'var(--text)' }}
              >
                {stats.averageRating || 0}
              </div>
              <div className="flex justify-center mb-3">
                <StarRating rating={stats.averageRating || 0} size="lg" />
              </div>
              <div className="text-sm" style={{ color: 'var(--muted)' }}>
                Based on {stats.totalReviews} client reviews
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl p-6"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            <h3
              className="font-semibold mb-4 text-center"
              style={{ color: 'var(--text)' }}
            >
              Rating Breakdown
            </h3>
            <div className="px-2">
              <RatingDistribution
                ratingsData={ratingsData}
                totalReviews={stats.totalReviews}
              />
            </div>
          </div>
        </div>

        {approvedReviews.length > 0 ? (
          <div className="relative">
            <button
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 lg:-translate-x-5 z-20 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm"
              style={{
                backgroundColor: 'var(--bg)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            <button
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 lg:-translate-x-5 z-20 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm"
              style={{
                backgroundColor: 'var(--bg)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
              onClick={() => swiperRef.current?.slideNext()}
            >
              <FiChevronRight className="w-5 h-5" />
            </button>

            <Swiper
              modules={[Autoplay, Pagination, Navigation, EffectCreative]}
              spaceBetween={24}
              slidesPerView={1}
              centeredSlides={true}
              loop={true}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              speed={800}
              pagination={{ clickable: true, dynamicBullets: true }}
              breakpoints={{
                640: { slidesPerView: 1.2, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 24 },
                1024: { slidesPerView: 2.5, spaceBetween: 24 },
                1280: { slidesPerView: 3, spaceBetween: 24 },
              }}
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              className="pb-12"
            >
              {approvedReviews.map((review, idx) => (
                <SwiperSlide key={review.id}>
                  <ReviewCard review={review} index={idx} />
                </SwiperSlide>
              ))}
            </Swiper>

            <div className="flex justify-center gap-2 mt-4">
              <div className="swiper-pagination !relative !bottom-0" />
            </div>
          </div>
        ) : (
          <div
            className="text-center py-12 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <FiMessageCircle
              className="w-12 h-12 mx-auto mb-3 opacity-50"
              style={{ color: 'var(--muted)' }}
            />
            <p style={{ color: 'var(--muted)' }}>
              No reviews yet. Be the first to review!
            </p>
          </div>
        )}

        <div className="text-center mt-12">
          <button
            onClick={() => setShowFormModal(true)}
            className="group relative inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 hover:gap-3 overflow-hidden"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            <span className="relative z-10">Write a Review</span>
            <FiStar className="relative z-10 w-4 h-4 transition-transform group-hover:rotate-12" />
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-shimmer" />
            </div>
          </button>
        </div>
      </div>

      <ReviewFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSubmit={handleReviewSubmit}
      />
    </div>
  );
};

export default ReviewsSection;
